import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { MarkdownText } from '@/components/common/MarkdownText';
import { formatResearchSummaryToMarkdown } from '@/lib/format-research-summary';
import { cn } from '@/lib/utils';
import { api } from '@/lib/api';
import { researchProviderLabel } from '@/lib/research-ui-defaults';
import type { ApiResponse } from '@/types';
import type { ResearchRun } from '@/types';
import { toast } from 'sonner';
import {
  Copy,
  Check,
  Globe,
  Link as LinkIcon,
  Loader2,
  MoreVertical,
  Sparkles,
  Star,
  Trash2,
  ListTodo,
} from 'lucide-react';
import {
  RunStatusIcon,
  STALE_RESEARCH_MS,
  STALE_RESEARCH_AZURE_COGNITIVE_MS,
  formatDurationMs,
} from './research-shared';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';

type PlanFollowUp = {
  summary?: string;
  model?: string;
  generated_at?: string;
};

function readFollowUp(plan: ResearchRun['plan']): PlanFollowUp | null {
  if (!plan || typeof plan !== 'object' || Array.isArray(plan)) return null;
  const fu = (plan as { follow_up?: PlanFollowUp }).follow_up;
  if (!fu || typeof fu !== 'object') return null;
  return fu.summary ? fu : null;
}

export function ResearchRunDetailPanel({
  run,
  scope,
  strategyId,
  onUpdated,
  onRequestDelete,
  onToggleStar,
  starBusy,
}: {
  run: ResearchRun;
  scope: 'strategy' | 'standalone';
  strategyId?: string;
  onUpdated: () => void;
  onRequestDelete?: (run: ResearchRun) => void;
  onToggleStar?: (run: ResearchRun) => void;
  starBusy?: boolean;
}) {
  const [cancelling, setCancelling] = useState(false);
  const [followUpLoading, setFollowUpLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [standaloneSources, setStandaloneSources] = useState<Array<{ title: string; url?: string | null }>>([]);
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  const [plans, setPlans] = useState<Array<{ id: string; title: string; year: number }>>([]);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [creatingActivity, setCreatingActivity] = useState(false);

  useEffect(() => {
    if (!activityDialogOpen) return;
    let cancelled = false;
    void api
      .get<ApiResponse<Array<{ id: string; title: string; year: number }>>>('/marketing-plans?limit=100&sortBy=year&order=desc')
      .then((res) => {
        if (cancelled || !res.success || !res.data) return;
        const list = Array.isArray(res.data) ? res.data : [];
        setPlans(list);
        if (list[0]?.id) setSelectedPlanId(list[0].id);
      })
      .catch(() => {
        if (!cancelled) setPlans([]);
      });
    return () => {
      cancelled = true;
    };
  }, [activityDialogOpen]);

  useEffect(() => {
    if (scope !== 'standalone' || run.status !== 'completed') {
      setStandaloneSources([]);
      return;
    }
    let cancelled = false;
    void api
      .get<
        ApiResponse<{ run: unknown; sources: Array<{ title: string; url?: string | null }> }>
      >(`/ai/research-runs/${run.id}`)
      .then((res) => {
        if (cancelled || !res.success || !res.data) return;
        setStandaloneSources(res.data.sources ?? []);
      })
      .catch(() => {
        if (!cancelled) setStandaloneSources([]);
      });
    return () => {
      cancelled = true;
    };
  }, [scope, run.id, run.status]);

  const started = run.started_at ? new Date(run.started_at).getTime() : new Date(run.created_at).getTime();
  const elapsedMs = Date.now() - started;
  const staleThresholdMs =
    run.provider === 'azure_cognitive_services' ? STALE_RESEARCH_AZURE_COGNITIVE_MS : STALE_RESEARCH_MS;
  const isStale = run.status === 'running' && elapsedMs > staleThresholdMs;
  const planMsg = typeof run.plan?.message === 'string' ? run.plan.message : null;
  const planUpdated = run.plan?.updated_at ? new Date(run.plan.updated_at).toLocaleString('de-CH') : null;
  const followUp = readFollowUp(run.plan);

  const handleCancel = async () => {
    if (scope !== 'strategy' || !strategyId) return;
    setCancelling(true);
    try {
      await api.post<ApiResponse>(`/marketing-strategies/${strategyId}/research/${run.id}/cancel`);
      toast.info('Abbruch gespeichert.');
      onUpdated();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Konnte nicht abbrechen.');
    } finally {
      setCancelling(false);
    }
  };

  const handleCopyQuery = async () => {
    try {
      await navigator.clipboard.writeText(run.query);
      setCopied(true);
      toast.success('Frage in die Zwischenablage kopiert.');
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Kopieren fehlgeschlagen.');
    }
  };

  const handleCreateActivity = async () => {
    if (!selectedPlanId) {
      toast.error('Bitte einen Marketingplan wählen.');
      return;
    }
    setCreatingActivity(true);
    try {
      const res = await api.post<ApiResponse<{ id: string }>>('/activities/from-research-run', {
        research_run_id: run.id,
        plan_id: selectedPlanId,
      });
      if (!res.success || !res.data?.id) {
        toast.error(res.error ?? 'Aktivität konnte nicht erstellt werden.');
        return;
      }
      toast.success('Aktivität aus Research angelegt.');
      setActivityDialogOpen(false);
      window.location.hash = `/activities/${res.data.id}`;
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Aktivität konnte nicht erstellt werden.');
    } finally {
      setCreatingActivity(false);
    }
  };

  const handleFollowUp = async () => {
    setFollowUpLoading(true);
    try {
      const path =
        scope === 'strategy' && strategyId
          ? `/marketing-strategies/${strategyId}/research/${run.id}/follow-up`
          : `/ai/research-runs/${run.id}/follow-up`;
      const res = await api.post<ApiResponse<{ follow_up: PlanFollowUp }>>(path, {});
      if (!res.success) {
        toast.error(res.error ?? 'Follow-up fehlgeschlagen.');
        return;
      }
      toast.success('Prognose & Umsetzung gespeichert.');
      onUpdated();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Follow-up fehlgeschlagen.');
    } finally {
      setFollowUpLoading(false);
    }
  };

  return (
    <TooltipProvider delayDuration={300}>
    <div className="flex min-h-0 flex-1 flex-col rounded-lg border border-border bg-card">
      <div className="flex shrink-0 flex-col gap-2 border-b border-border px-4 py-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <div className="mt-0.5 shrink-0">
            <RunStatusIcon status={run.status} />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground">{run.query}</p>
            <p className="mt-1 text-xs text-muted">
              {researchProviderLabel(run.provider)} · {new Date(run.created_at).toLocaleDateString('de-CH')}
              {run.status === 'completed' && run.confidence != null
                ? ` · ${Math.round(run.confidence * 100)}% Konfidenz`
                : ''}
            </p>
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5 sm:justify-start">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 text-muted hover:text-foreground"
                onClick={() => void handleCopyQuery()}
                aria-label="Frage kopieren"
              >
                {copied ? <Check size={16} className="text-muted dark:text-foreground" /> : <Copy size={16} />}
              </Button>
            </TooltipTrigger>
            <TooltipContent>Frage kopieren</TooltipContent>
          </Tooltip>
          {onToggleStar ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className={cn(
                    'h-8 w-8 p-0',
                    run.is_starred ? 'text-warning' : 'text-muted hover:text-warning'
                  )}
                  disabled={starBusy}
                  onClick={() => onToggleStar(run)}
                  aria-label={run.is_starred ? 'Favorit entfernen' : 'Als Favorit'}
                >
                  {starBusy ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Star size={16} className={run.is_starred ? 'fill-warning text-warning' : ''} />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>{run.is_starred ? 'Favorit entfernen' : 'Als Favorit'}</TooltipContent>
            </Tooltip>
          ) : null}
          {onRequestDelete ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 text-muted"
                  aria-label="Weitere Aktionen"
                >
                  <MoreVertical size={16} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44">
                <DropdownMenuItem
                  className="gap-2 text-xs text-danger focus:text-danger"
                  onClick={() => onRequestDelete(run)}
                >
                  <Trash2 size={14} /> Löschen
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
          {run.status === 'completed' && run.summary && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="h-8 gap-1 text-xs"
              disabled={followUpLoading}
              onClick={() => void handleFollowUp()}
            >
              {followUpLoading ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
              Prognose &amp; Umsetzung
            </Button>
          )}
          {run.status === 'completed' && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 gap-1 text-xs"
              onClick={() => setActivityDialogOpen(true)}
            >
              <ListTodo size={12} />
              Als Aktivität
            </Button>
          )}
          {run.status === 'running' && scope === 'strategy' && strategyId && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 shrink-0 text-xs"
              disabled={cancelling}
              onClick={() => void handleCancel()}
            >
              {cancelling ? <Loader2 size={12} className="animate-spin" /> : 'Abbrechen'}
            </Button>
          )}
        </div>
      </div>

      <Dialog open={activityDialogOpen} onOpenChange={setActivityDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Aktivität aus Research</DialogTitle>
            <DialogDescription>
              Legt eine neue Aktivität im gewählten Marketingplan an und übernimmt die Research-Zusammenfassung in die
              Beschreibung.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-2 py-2">
            <Label htmlFor="research-plan">Marketingplan</Label>
            <Select value={selectedPlanId} onValueChange={setSelectedPlanId}>
              <SelectTrigger id="research-plan">
                <SelectValue placeholder="Plan wählen" />
              </SelectTrigger>
              <SelectContent>
                {plans.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.title} ({p.year})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setActivityDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button type="button" disabled={creatingActivity || !selectedPlanId} onClick={() => void handleCreateActivity()}>
              {creatingActivity ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Anlegen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {run.status === 'running' && (
          <div className="space-y-2 px-4 py-3 text-xs">
            <div className="space-y-2 rounded-md border border-border bg-background p-3 dark:bg-card/50">
              <p className="font-medium text-foreground">Status</p>
              <p className="text-muted">{planMsg ?? 'Warte auf Antwort vom KI-Provider …'}</p>
              <p className="text-muted">
                Laufzeit: <span className="font-mono tabular-nums">{formatDurationMs(elapsedMs)}</span>
                {planUpdated ? <> · Letzte Aktualisierung: {planUpdated}</> : null}
              </p>
              <p className="text-muted">
                {run.provider === 'azure_cognitive_services' ? (
                  <>
                    Bei <strong>Azure Responses</strong> (gpt-5.4-pro) sind <strong>5–15 Minuten</strong> häufig normal.
                    Server-Timeout (Standard): 45 Min.
                  </>
                ) : (
                  <>
                    Erwartete Bearbeitung: meist wenige Minuten (Timeout serverseitig{' '}
                    {run.provider === 'azure_foundry' ? '20' : '30'} Min.).
                  </>
                )}
              </p>
              {isStale && (
                <p className="rounded border border-warning/30 bg-warning-soft p-2 text-warning">
                  Ungewöhnlich lange Laufzeit — du kannst den Lauf abbrechen oder die Provider-Credentials prüfen.
                </p>
              )}
            </div>
          </div>
        )}

        {run.status === 'failed' && run.error_message && (
          <div className="px-4 py-3">
            <div className="rounded-md border border-danger/30 bg-danger-soft p-3 text-sm text-danger">
              <p className="font-medium">Fehler</p>
              <p className="mt-1 whitespace-pre-wrap">{run.error_message}</p>
            </div>
          </div>
        )}

        {run.status === 'cancelled' && (
          <div className="px-4 py-3">
            <p className="text-sm text-muted">
              {run.error_message ?? 'Dieser Research-Lauf wurde abgebrochen.'}
            </p>
          </div>
        )}

        {run.status === 'completed' && run.summary && (
          <div className="border-t border-border px-4 py-4">
            <p className="mb-3 text-xs font-medium text-muted">
              Research-Ergebnis
            </p>
            <div className="text-sm leading-relaxed text-foreground">
              <MarkdownText
                content={formatResearchSummaryToMarkdown(run.summary)}
                className="space-y-3 [&_h2]:mt-4 [&_h2]:first:mt-0 [&_h2]:text-sm [&_h2]:font-semibold [&_h2]:text-foreground dark:[&_h2]:text-primary-foreground [&_li]:my-1 [&_p]:text-muted dark:[&_p]:text-muted [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5"
              />
            </div>
            {scope === 'standalone' && standaloneSources.length > 0 && (
              <div className="mt-4 border-t border-border pt-3">
                <h4 className="mb-2 flex items-center gap-2 text-xs font-medium text-muted">
                  <Globe size={13} /> Quellen ({standaloneSources.length})
                </h4>
                <ul className="space-y-1.5">
                  {standaloneSources.map((s, i) => (
                    <li key={i} className="text-xs">
                      {s.url ? (
                        <a
                          href={s.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-start gap-1.5 text-foreground underline-offset-2 hover:underline"
                        >
                          <LinkIcon size={11} className="mt-0.5 shrink-0" />
                          {s.title}
                        </a>
                      ) : (
                        <span className="text-muted">{s.title}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {followUp?.summary && (
          <div className="border-t border-border px-4 py-4">
            <p className="mb-3 text-xs font-medium text-muted">
              Prognose &amp; Umsetzung
            </p>
            <div className="text-sm leading-relaxed">
              <MarkdownText
                content={formatResearchSummaryToMarkdown(followUp.summary)}
                className="space-y-3 [&_h2]:mt-4 [&_h2]:first:mt-0 [&_h2]:text-sm [&_h2]:font-semibold [&_h2]:text-foreground dark:[&_h2]:text-primary-foreground [&_li]:my-1 [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5"
              />
            </div>
            {followUp.generated_at && (
              <p className="mt-2 text-[10px] text-muted">
                {new Date(followUp.generated_at).toLocaleString('de-CH')}
                {followUp.model ? ` · ${followUp.model}` : ''}
              </p>
            )}
          </div>
        )}

        {run.status === 'completed' && !run.summary && (
          <div className="border-t border-warning/30 bg-warning-soft px-4 py-3">
            <p className="text-sm font-medium text-warning">Kein Text gespeichert</p>
            <p className="mt-1 text-xs text-warning/90">
              Dieser Lauf wurde als abgeschlossen markiert, ohne speicherbaren Inhalt. Bitte erneut versuchen.
            </p>
          </div>
        )}
      </div>
    </div>
    </TooltipProvider>
  );
}
